import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getCenter } from '../../../../api/centers/get-center'
import { getCenterTotalCosts } from '../../../../api/centers/get-center-total-costs'
import useAuth from '../../../../lib/hooks/use-auth'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import useTranslate from '../../../../lib/hooks/use-translate'
import { Center } from '../../../../lib/types/models/center/center'
import { DataProps } from '../../../../lib/types/utils'
import { useCenterCosts } from './center.hooks'
import { CenterCostsTableProps, getFinalBalanceBadgeColor } from './utils'

type Props = Omit<CenterCostsTableProps, 'mode' | 'costType'>

export function CenterFinalCostsBalance(props: DataProps<Center, Props>) {
  const { currency } = props
  const { t } = useTranslate()
  const { user } = useAuth()

  const { id: centerId, data: center } = useDataProps(props, (id) => getCenter(id, 'FULL'))

  const { centerCostsView } = useCenterCosts({ centerId, costType: 'final-cost', table: 'costs' })

  const { data: totalCosts } = useQuery({
    queryKey: ['center-grand-total-cost', centerId],
    queryFn: () => getCenterTotalCosts(centerId)
  })

  if (!center || !user || !centerCostsView.data) return null
  return (
    <div className={'flex flex-row items-start justify-between gap-2'}>
      <Badge
        text={t('center.costs.final.balance.title.final.balance')}
        variant={'white'}
        textColor={tailwindColors.fushia.DEFAULT}
        color={tailwindColors.white.hover}
      />

      <div className={'flex flex-col items-end gap-2'}>
        <div className={'flex flex-row items-center gap-2 border border-grey-light rounded p-2'}>
          <Typography variant={'secondary-body'}>
            {t('center.costs.final.balance.total.amounts.previously.transferred')}
          </Typography>
          <Badge
            variant={'fushia'}
            text={
              totalCosts?.totalPayedBalance !== undefined && totalCosts?.totalPayedBalance !== null
                ? NumberFormatter.formatCurrency(totalCosts.totalPayedBalance, currency)
                : '-'
            }
          />
        </div>
        <div className={'flex flex-row items-center gap-2 border border-grey-light rounded p-2'}>
          <Typography variant={'secondary-body'}>
            {t('center.costs.final.balance.total.final.amount')}
          </Typography>
          <Badge
            variant={'fushia'}
            text={
              totalCosts?.finalTotalCost !== undefined && totalCosts?.finalTotalCost !== null
                ? NumberFormatter.formatCurrency(totalCosts.finalTotalCost, currency)
                : '-'
            }
          />
        </div>
        <div className={'flex flex-row items-center gap-2 border border-grey-light rounded p-2'}>
          <Typography variant={'secondary-body'}>
            {t('center.costs.final.balance.total.balance')}
          </Typography>
          <Badge
            variant={'primary'}
            text={
              totalCosts?.finalBalance !== undefined && totalCosts?.finalBalance !== null
                ? NumberFormatter.formatCurrency(totalCosts.finalBalance, currency)
                : '-'
            }
            {...getFinalBalanceBadgeColor(totalCosts?.finalBalance)}
          />
        </div>
        <Typography variant={'small-medium'} color={tailwindColors.grey.lighter}>
          {t('center.costs.final.balance.note')}
        </Typography>
      </div>
    </div>
  )
}
